// Google Analytics utilities for NettoiGo
// This works alongside the existing Swetrix analytics system

import { ANALYTICS_CONFIG } from "@/config/analytics";

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = ANALYTICS_CONFIG.GOOGLE_ANALYTICS_ID;

/**
 * Check if Google Analytics is loaded and available
 */
export const isGoogleAnalyticsLoaded = (): boolean => {
  return (
    typeof window !== "undefined" &&
    typeof window.gtag === "function" &&
    ANALYTICS_CONFIG.ENABLE_GOOGLE_ANALYTICS
  );
};

/**
 * Track a page view in Google Analytics
 * @param url - The page URL
 * @param title - The page title
 */
export const trackPageView = (url: string, title?: string) => {
  if (!isGoogleAnalyticsLoaded()) {
    if (ANALYTICS_CONFIG.DEBUG_ANALYTICS) {
      console.log("GA Page View (dev):", { url, title });
    }
    return;
  }

  try {
    window.gtag("config", GA_MEASUREMENT_ID, {
      page_location: url,
      page_title: title,
    });

    if (ANALYTICS_CONFIG.DEBUG_ANALYTICS) {
      console.log("GA Page View tracked:", { url, title });
    }
  } catch (error) {
    console.error("Error tracking page view in Google Analytics:", error);
  }
};

/**
 * Track a custom event in Google Analytics
 * @param eventName - The event name
 * @param parameters - Event parameters
 */
export const trackEvent = (
  eventName: string,
  parameters: Record<string, any> = {}
) => {
  if (!isGoogleAnalyticsLoaded()) {
    if (process.env.NODE_ENV === "development") {
      console.log("GA Event (dev):", eventName, parameters);
    }
    return;
  }

  try {
    window.gtag("event", eventName, {
      ...parameters,
      send_to: GA_MEASUREMENT_ID,
    });

    if (process.env.NODE_ENV === "development") {
      console.log("GA Event tracked:", eventName, parameters);
    }
  } catch (error) {
    console.error("Error tracking event in Google Analytics:", error);
  }
};

/**
 * Track conversion events (for Google Ads)
 * @param conversionLabel - The conversion label from Google Ads
 * @param value - Optional value for the conversion
 * @param currency - Currency code (default: CAD)
 */
export const trackConversion = (
  conversionLabel: string,
  value?: number,
  currency: string = "CAD"
) => {
  if (!isGoogleAnalyticsLoaded()) {
    if (process.env.NODE_ENV === "development") {
      console.log("GA Conversion (dev):", { conversionLabel, value, currency });
    }
    return;
  }

  try {
    const conversionData: Record<string, any> = {
      send_to: `${GA_MEASUREMENT_ID}/${conversionLabel}`,
    };

    if (value !== undefined) {
      conversionData.value = value;
      conversionData.currency = currency;
    }

    window.gtag("event", "conversion", conversionData);

    if (process.env.NODE_ENV === "development") {
      console.log("GA Conversion tracked:", conversionData);
    }
  } catch (error) {
    console.error("Error tracking conversion in Google Analytics:", error);
  }
};

// Pre-defined event tracking functions for common actions

/**
 * Track user signup
 */
export const trackSignup = (method: string = "email") => {
  trackEvent("sign_up", {
    method,
  });
};

/**
 * Track user login
 */
export const trackLogin = (method: string = "email") => {
  trackEvent("login", {
    method,
  });
};

/**
 * Track booking start
 */
export const trackBookingStart = (cleanerId?: string, service?: string) => {
  trackEvent("begin_checkout", {
    cleaner_id: cleanerId,
    service_type: service,
  });
};

/**
 * Track booking completion
 */
export const trackBookingComplete = (
  bookingId: string,
  cleanerId: string,
  value: number,
  currency: string = "CAD"
) => {
  trackEvent("purchase", {
    transaction_id: bookingId,
    value,
    currency,
    cleaner_id: cleanerId,
  });
};

/**
 * Track search
 */
export const trackSearch = (
  searchTerm: string,
  filters?: Record<string, any>
) => {
  trackEvent("search", {
    search_term: searchTerm,
    ...filters,
  });
};

/**
 * Track content view (e.g., cleaner profile)
 */
export const trackViewContent = (
  contentType: string,
  contentId: string,
  contentName?: string
) => {
  trackEvent("view_item", {
    content_type: contentType,
    item_id: contentId,
    item_name: contentName,
  });
};

/**
 * Track contact/inquiry
 */
export const trackContact = (method: string = "chat") => {
  trackEvent("contact", {
    method,
  });
};

/**
 * Set user properties for analytics
 */
export const setUserProperties = (properties: Record<string, any>) => {
  if (!isGoogleAnalyticsLoaded()) {
    return;
  }

  try {
    window.gtag("config", GA_MEASUREMENT_ID, {
      custom_map: properties,
    });
  } catch (error) {
    console.error("Error setting user properties in Google Analytics:", error);
  }
};
